/**
 * Dagdeel-telling (Spoor 2, Verlof MVP — Epic 4, Story 4.2)
 *
 * AC 2/AC 3: telt per dag "kantoor: N · thuis: N · afwezig: N" met
 * dagdeel-precisie (elke medewerker draagt per dagdeel 0,5 bij aan
 * precies één categorie, of aan geen enkele). `verlof_in_afwachting` telt
 * NOOIT mee als afwezig (AC 2, kritiek) — de onderliggende rooster-locatie
 * (kantoor/thuis) telt gewoon door, zie `calendarService`'s Story
 * 4.2-uitbreiding op `AttendanceDagdeelEntry.location`. `niet_werkbaar_
 * patroon`/`weekend` dragen niets bij aan enige categorie (ze waren nooit
 * "verwacht aanwezig").
 */

import type {
  AttendanceCalendarResponse,
  AttendanceDagdeelEntry,
  AttendanceEmployeeEntry,
} from '@functieloonhuis/shared';

export interface DayTelling {
  kantoor: number;
  thuis: number;
  afwezig: number;
}

type Bucket = keyof DayTelling;

/**
 * Which telling-bucket a single dagdeel contributes 0,5 to, or `null` for none.
 *
 * Exported since the aanwezig/afwezig row-filter hangs off this exact
 * definition — see `matchesAanwezigheid`. Deliberately ONE source of truth:
 * a second "what counts as afwezig" rule would let the visible counter and
 * the filtered rows contradict each other.
 */
export function dagdeelBucket(entry: AttendanceDagdeelEntry): Bucket | null {
  if (entry.status === 'werkt' || entry.status === 'verlof_in_afwachting') {
    // in_afwachting carries the underlying rooster-location too (Story 4.2
    // telling-vereiste) — an undecided request is not yet "afwezig".
    return entry.location === 'thuis' ? 'thuis' : 'kantoor';
  }
  if (entry.status === 'verlof_goedgekeurd' || entry.status === 'feestdag' || entry.status === 'ziek') {
    // Ziek telt mee als afwezig, net als verlof_goedgekeurd/feestdag
    // (Epic nmbrs-verzuim-kalender, Story 1.3, AC 3).
    return 'afwezig';
  }
  if (entry.status === 'niet_werkbaar_patroon' || entry.status === 'weekend') {
    // Never expected present, not counted.
    return null;
  }
  // Deliberately non-throwing (unlike AanwezigheidCell.HalfCell/statusLabel,
  // Story 1.3 code-review finding): this aggregates across every employee ×
  // dagdeel for one summary strip — a throw here would crash the WHOLE
  // strip for a single stale/unrecognized entry, worse than the render-path
  // case (which is now contained to one grid cell via CellErrorBoundary,
  // but no equivalent per-entry containment exists for a running total).
  // Still not SILENT, though: logged so a version-skew/stale-cache gap is
  // visible in the console instead of just quietly under-counting.
  console.error(`telling.dagdeelBucket: onbekende dagdeel-status "${entry.status}", niet meegeteld`);
  return null;
}

/**
 * Aggregate the dagdeel-telling for `date` across every employee in
 * `response`. Returns `{kantoor: 0, thuis: 0, afwezig: 0}` when the date
 * isn't present for any employee (e.g. outside the fetched range).
 */
export function tellForDate(response: AttendanceCalendarResponse, date: string): DayTelling {
  const totals: DayTelling = { kantoor: 0, thuis: 0, afwezig: 0 };

  for (const employeeEntry of response.employees) {
    const day = employeeEntry.days.find((d) => d.date === date);
    if (!day) continue;

    for (const dagdeel of [day.morning, day.afternoon]) {
      const bucket = dagdeelBucket(dagdeel);
      if (bucket) {
        totals[bucket] += 0.5;
      }
    }
  }

  return totals;
}

/** Row-filter mode for the aanwezigheidskalender. */
export type AanwezigheidFilterMode = 'alle' | 'aanwezig' | 'afwezig';

/**
 * Does `employeeEntry` match `mode` over the currently shown `dates`?
 *
 * Over a week or month nobody is purely present or purely absent, so the
 * rule is "at least one dagdeel in the shown period" (besluit Friso,
 * 2026-07-29). Buckets come from `dagdeelBucket`, so this always agrees
 * with the telling-strip beneath the grid.
 *
 * Note: `feestdag` counts as afwezig there, so in a week containing a
 * public holiday every employee matches 'afwezig'. That is factually
 * correct and deliberately not special-cased — diverging from the visible
 * counter would be worse than the mild surprise.
 *
 * Dagdelen that were never "expected present" (`niet_werkbaar_patroon`,
 * `weekend`) contribute to neither mode, exactly as they contribute to no
 * telling-bucket. So a day-view on a Saturday, or someone structurally
 * free for the whole shown period, matches neither 'aanwezig' nor
 * 'afwezig' — the page's "geen medewerkers met de huidige filters"-state
 * then explains the empty result.
 *
 * `dates` is a Set rather than an array because the caller runs this once
 * per employee; rebuilding the lookup inside would be O(employees × dates).
 */
export function matchesAanwezigheid(
  employeeEntry: AttendanceEmployeeEntry,
  dates: ReadonlySet<string>,
  mode: AanwezigheidFilterMode,
): boolean {
  if (mode === 'alle') return true;

  for (const day of employeeEntry.days) {
    if (!dates.has(day.date)) continue;

    for (const dagdeel of [day.morning, day.afternoon]) {
      const bucket = dagdeelBucket(dagdeel);
      if (bucket === null) continue;

      if (mode === 'afwezig' ? bucket === 'afwezig' : bucket !== 'afwezig') {
        return true;
      }
    }
  }

  return false;
}
